'use client';

import { useEffect, useRef, useState } from 'react';
import type { SceneStats } from '@config3d/scene';

export type PlayerDebugDprMode = 'default' | '1' | '1.5';
export type PlayerDebugTexMode = 'embed' | 'full' | '2k' | '1k' | 'half' | 'quarter';

interface Props {
  sceneStats?: SceneStats | null;
  dprMode: PlayerDebugDprMode;
  onDprMode: (m: PlayerDebugDprMode) => void;
  shadowsOn: boolean;
  onShadowsOn: (v: boolean) => void;
  postfxOn: boolean;
  onPostfxOn: (v: boolean) => void;
  transmissionFull: boolean;
  onTransmissionFull: (v: boolean) => void;
  texMode: PlayerDebugTexMode;
  onTexMode: (m: PlayerDebugTexMode) => void;
  lowPowerGl: boolean;
  onLowPowerGl: (v: boolean) => void;
}

function useSmoothedFps(): { fps: number; frameMs: number } {
  const [out, setOut] = useState({ fps: 0, frameMs: 0 });
  const framesRef = useRef<number[]>([]);
  const rafRef = useRef(0);

  useEffect(() => {
    let last = performance.now();
    function tick() {
      const now = performance.now();
      const delta = now - last;
      last = now;
      framesRef.current.push(delta);
      if (framesRef.current.length > 60) framesRef.current.shift();
      const avg = framesRef.current.reduce((a, b) => a + b, 0) / framesRef.current.length;
      setOut({ fps: Math.round(1000 / avg), frameMs: Math.round(avg * 10) / 10 });
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return out;
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[9px] text-zinc-500 uppercase">{label}</div>
      <div className="flex flex-wrap gap-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
              value === o.v
                ? 'bg-white/20 border-white/40 text-white'
                : 'bg-black/30 border-white/10 text-zinc-300 hover:border-white/25'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-center justify-between gap-2 cursor-pointer text-[11px]">
      <span className="text-zinc-300">
        {label}
        {hint && <span className="block text-[8px] text-zinc-500 font-normal mt-0.5">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-zinc-500"
      />
    </label>
  );
}

export function PlayerDebugPanel({
  sceneStats,
  dprMode,
  onDprMode,
  shadowsOn,
  onShadowsOn,
  postfxOn,
  onPostfxOn,
  transmissionFull,
  onTransmissionFull,
  texMode,
  onTexMode,
  lowPowerGl,
  onLowPowerGl,
}: Props) {
  const { fps, frameMs } = useSmoothedFps();
  const fpsColor = fps >= 55 ? 'text-green-400' : fps >= 30 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div
      className="pointer-events-auto absolute bottom-2 left-2 z-[80] max-w-[min(100%-1rem,280px)] rounded-lg border border-white/15 bg-black/80 px-2.5 py-2 font-mono text-[11px] text-white shadow-lg backdrop-blur-sm select-none"
      role="region"
      aria-label="Player debug"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-white/10 pb-1">
        <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Scene debug</span>
        <span className={`text-sm font-semibold tabular-nums ${fpsColor}`}>{fps}</span>
      </div>
      <div className="mb-2 flex justify-between text-[10px] text-zinc-400">
        <span>Frame</span>
        <span className="text-zinc-200">{frameMs}ms</span>
      </div>

      <Segmented
        label="DPR cap"
        value={dprMode}
        onChange={onDprMode}
        options={[
          { v: 'default', label: 'Auto' },
          { v: '1', label: '1' },
          { v: '1.5', label: '1.5' },
        ]}
      />

      <div className="mt-2 space-y-1.5 border-t border-white/10 pt-2">
        <ToggleRow label="Shadows" checked={shadowsOn} onChange={onShadowsOn} />
        <ToggleRow
          label="Post-processing"
          checked={postfxOn}
          onChange={onPostfxOn}
          hint="Off remounts GL with MSAA disabled. Bloom/SSAO are already off on this product."
        />
        <ToggleRow label="Transmission (full res)" checked={transmissionFull} onChange={onTransmissionFull} />
        <ToggleRow
          label="Low-power GPU hint"
          checked={lowPowerGl}
          onChange={onLowPowerGl}
          hint="May need reload to apply"
        />
      </div>

      <div className="mt-2 border-t border-white/10 pt-2">
        <label className="block text-[9px] uppercase text-zinc-500 mb-0.5">Textures</label>
        <select
          className="w-full rounded border border-zinc-600 bg-zinc-900 px-1.5 py-1 text-[11px] text-white"
          value={texMode}
          onChange={(e) => onTexMode(e.target.value as PlayerDebugTexMode)}
        >
          <option value="embed">Embed / URL default</option>
          <option value="full">Full</option>
          <option value="2k">Cap 2K</option>
          <option value="1k">Cap 1K</option>
          <option value="half">Scale 50%</option>
          <option value="quarter">Scale 25%</option>
        </select>
      </div>

      {sceneStats?.aa && (
        <div className="mt-2 border-t border-white/10 pt-2 text-[10px] text-zinc-400 space-y-0.5">
          <div className="text-[9px] uppercase text-zinc-500">Anti-alias</div>
          <div className="flex justify-between gap-2">
            <span>Mode / composer</span>
            <span className="text-zinc-200">
              {sceneStats.aa.mode}
              {sceneStats.aa.composer ? ' + composer' : ' native'}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span>GL samples</span>
            <span className={sceneStats.aa.samples < 2 ? 'text-red-400' : 'text-zinc-200'}>
              {sceneStats.aa.samples}× {sceneStats.aa.nativeAntialias ? '(antialias on)' : '(antialias off)'}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span>Buffer / CSS</span>
            <span className="text-zinc-200 tabular-nums">
              {sceneStats.aa.bufW}×{sceneStats.aa.bufH} @ {sceneStats.aa.pixelRatio} ({sceneStats.aa.cssW}×{sceneStats.aa.cssH})
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span>LOD bias / asked MSAA</span>
            <span className="text-zinc-200">
              {sceneStats.aa.lodBias} / {sceneStats.aa.requestedMsaa}×
            </span>
          </div>
          {sceneStats.aa.samples < 2 && !sceneStats.aa.composer && (
            <p className="pt-0.5 text-[9px] leading-snug text-amber-300/90">
              Native MSAA is not on this framebuffer (samples=0). Edges will look 1× aliased.
            </p>
          )}
          {sceneStats.aa.composer && (
            <p className="pt-0.5 text-[9px] leading-snug text-zinc-500">
              Composer owns the frame: {sceneStats.aa.mode === 'smaa' ? '4× MSAA + SMAA soften' : 'native canvas MSAA is off'}.
            </p>
          )}
        </div>
      )}
      {sceneStats && (
        <div className="mt-2 border-t border-white/10 pt-2 text-[10px] text-zinc-400 space-y-0.5">
          <div className="flex justify-between gap-2">
            <span>Δ tris</span>
            <span className="text-zinc-200 truncate">
              {sceneStats.triangles.toLocaleString()} / {sceneStats.renderTriangles.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Draw calls</span>
            <span className="text-zinc-200">{sceneStats.drawCalls}</span>
          </div>
        </div>
      )}
    </div>
  );
}
